import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import PublicationExplorer from "./PublicationExplorer";
import PublicationDetail from "./PublicationDetail";
import { SITE_URL, updatePageMetadata } from "../../metadata";
import { useTranslation } from "../../i18n/LanguageProvider";
import { localizedAbstract, localizedTitle } from "./publicationTypes";

export default function PublicationPage({
  groupedByYear,
  title = "Publications",
  filterFn = () => true,
  basePath = "publications",
  showReference = true,
  explorer: Explorer = PublicationExplorer,
  formatLabel = () => null,
}) {
  const { t, language, isPortuguese } = useTranslation();
  const displayTitle = title === "Selected Talks" ? t("publications.talksTitle") : t("publications.heading");
  const [notFoundId, setNotFoundId] = useState(null);
  const { id } = useParams();

  const filteredByType = useMemo(() => {
    if (!groupedByYear) return {};
    return Object.entries(groupedByYear).reduce((acc, [year, entries]) => {
      const filtered = (entries || []).filter(filterFn);
      if (filtered.length) acc[year] = filtered;
      return acc;
    }, {});
  }, [groupedByYear, filterFn]);

  const selected = useMemo(() => {
    if (!id) return null;
    return Object.values(filteredByType).flat().find(entry => entry.id === id) || null;
  }, [id, filteredByType]);

  useEffect(() => {
    setNotFoundId(id && !selected ? id : null);
  }, [id, selected]);

  const baseTitle = `${displayTitle} | Frederico Schardong, PhD`;
  const baseDescription = title === "Selected Talks"
    ? t("metadata.pages.talks")
    : t("metadata.pages.publications");

  useEffect(() => {
    if (!id) {
      updatePageMetadata({ title: baseTitle, description: baseDescription, path: `/${basePath}`, language });
      return;
    }

    if (!selected) {
      updatePageMetadata({
        title: `${displayTitle} not found | Frederico Schardong, PhD`,
        description: t("publications.noItem", { title: displayTitle.toLowerCase(), id }),
        path: `/${basePath}/${id}`,
        language,
      });
      return;
    }

    updatePageMetadata({
      title: `${localizedTitle(selected, isPortuguese) || title} | Frederico Schardong, PhD`,
      description: (localizedAbstract(selected, isPortuguese) || baseDescription).replace(/\s+/g, " ").trim().slice(0, 300),
      path: `/${basePath}/${selected.id}`,
      image: selected.thumbnail ? `${SITE_URL}/${basePath}/thumbnail/${selected.thumbnail}` : undefined,
      type: "article",
      language,
    });
  }, [baseDescription, basePath, baseTitle, displayTitle, id, isPortuguese, language, selected, t, title]);

  return (
    <div className={`container ${`${basePath}`}-page`}>
      {selected ? (
        <PublicationDetail
          entry={selected}
          basePath={basePath}
          backTo={`/${basePath}`}
          backLabel={displayTitle}
          formatLabel={formatLabel(selected, isPortuguese)}
          showReference={showReference}
        />
      ) : (
        <>
          <h1 className="page-title">{displayTitle}</h1>
          {notFoundId && (
            <div className="alert alert-warning" role="alert">
              {t("publications.noItem", { title: displayTitle.toLowerCase(), id: notFoundId })}
            </div>
          )}
          <Explorer groupedByYear={filteredByType} basePath={basePath} />
        </>
      )}
    </div>
  );
}
